import s from './MediaCard.module.sass'

import React, { FC, HTMLProps, useMemo } from 'react'
import Link from '@shared/ui/Navigation/Link'

import { Favorite } from '@features/Favorite'

import { ChevronWithTooltip } from '@shared/ui/Feedback/ChevronWithTooltip'
import { BlockBoundary } from '@shared/lib/components/BlockBoundary'
import { IconButton } from '@shared/ui/Actions/IconButton'
import { PickAnalytic } from '@shared/api/analytics/types/types'
import { Rating } from '@shared/ui/Feedback/Rating'
import { Text } from '@shared/ui/Typography/Text'
import { TypeMappingMediaDataOutput } from '@shared/api/middleware/mappingAPI/news/type'
import { useClientResponsive } from '@shared/lib/hooks/useClientResponsive'
import { useCurrentSize } from '@shared/lib/helpers/classes'
import { isHardcodedChevronLink } from '../chevronLinks'

import IconSelectionChevronMedium from '@icons/icon-selection-chevron--medium.svg'
import IconLike from '@icons/icon-like.svg'
import { MediaImg } from '@shared/ui/Media/MediaImg'
import { ImageModel } from '@shared/ui/Media/MediaImg/types/types'

// 1x1, 2x1 for mobile
// type size = 'L1x1 L2x1 M1x1 M2x1 S1x1 1x1 2x1'
interface Props {
  id: string | number
  sizes: string
  title: string
  subtitle?: {
    name: string
    url?: string
  }
  date?: string
  hideImage?: boolean
  img: {
    desktop: {
      horizontal: string
      vertical: string
    }
    mobile: {
      vertical: string
    }
  }
  url: string
  favorite: {
    category: 'news' | 'selection'
    analytic: {
      data: TypeMappingMediaDataOutput
      other: PickAnalytic<'section_name'>
    }
  }
  promo?: boolean
  chevron?: boolean
  className?: string
}

const sizesImage = {
  L1x1: { width: 297, height: 297 },
  L2x1: { width: 610, height: 297 },
  M1x1: { width: 219, height: 219 },
  M2x1: { width: 453, height: 219 },
  S1x1: { width: 141, height: 141 },
  '1x1': { width: 158, height: 158 },
  '2x1': { width: 324, height: 158 },
} as const

type MediaCardSize = keyof typeof sizesImage

const MediaCard: FC<HTMLProps<HTMLDivElement> & Props> = React.forwardRef(
  (
    {
      id,
      sizes,
      title,
      subtitle,
      date,
      img,
      hideImage = false,
      favorite,
      url = '',
      promo = false,
      chevron = false,
      className = '',
      ...props
    },
    ref,
  ) => {
    const [size] = useCurrentSize(sizes)
    const { isMobile } = useClientResponsive()
    const isLongFormat = size === 'L2x1' || size === 'M2x1' || size === '2x1'
    const showChevron = chevron || isHardcodedChevronLink(url)

    const isShowSubtitle = subtitle.name

    const getPhotoLink = useMemo((): ImageModel => {
      let photoUrl: string
      if (isMobile) {
        photoUrl = img.mobile.vertical
      } else if (isLongFormat) {
        photoUrl = img.desktop.horizontal
      } else {
        photoUrl = img.desktop.vertical
      }

      const dimensions = sizesImage[size as MediaCardSize]

      return {
        url: photoUrl,
        width: dimensions.width,
        height: dimensions.height,
      }
    }, [isLongFormat, img, isMobile, size])

    return (
      <BlockBoundary>
        <div data-id={id} className={`${s.card} ${className}`} data-size={size} title={title} {...props} ref={ref}>
          <div className={s['image-wrapper']}>
            {promo && <Rating className={s.promo} mode={'fill'} isPromo sizes={'S'} />}

            {!!showChevron && (
              <ChevronWithTooltip icon={IconSelectionChevronMedium} className={s['afisha-selection-chevron--medium']} />
            )}

            <Favorite
              category={favorite.category}
              id={Number(id)}
              analytic={{ data: favorite.analytic.data, other: favorite.analytic.other }}
              className={s.favorite}>
              <IconButton sizes={'S'} mode={'white'} icon={IconLike} />
            </Favorite>

            <Link href={url} className={s['card-image-link']} scroll={false}>
              {!hideImage && <MediaImg image={getPhotoLink} className={s.image} flexible={true} />}
            </Link>
          </div>

          <div className={s['card-extra']}>
            <>
              {date && (
                <Text className={s['card-date']} sizes={'S'}>
                  {date}
                </Text>
              )}

              {date && isShowSubtitle && <span className={s['card-dot']}>·</span>}

              {subtitle.name &&
                (subtitle.url ? (
                  <Link href={subtitle.url}>
                    <Text className={s['card-media-type']} sizes={'S'}>
                      {subtitle.name}
                    </Text>
                  </Link>
                ) : (
                  <Text className={s['card-media-type']} sizes={'S'}>
                    {subtitle.name}
                  </Text>
                ))}
            </>
          </div>

          <div className={s['card-description']}>
            <Link href={url} scroll={false}>
              <Text sizes={'M M M S'}>{title}</Text>
            </Link>
          </div>
        </div>
      </BlockBoundary>
    )
  },
)

MediaCard.displayName = 'MediaCard'

export default MediaCard
